// StabilizerPreference — 手ぶれ補正 (stroke stabilization) のレベルを per-user で
// localStorage に永続化する infrastructure 層ユーティリティ.
//
// 背景 (docs/_wip/modern-layout.md §9.8、consult 2026-06-08):
//   - 手ぶれ補正は **β Modern feature** (`ModernToolProperties` のモディファイア枠で
//     off/弱/中/強 を選ぶ、§9.3 で予約済の枠)。Classic は懐古純度のため UI 非露出。
//   - 設定は **per-user localStorage** (penPressure / layoutPreference と同流儀)、
//     **default off** (= 有効化しない限り現挙動と同じ)。
//   - engine は §9.7 で共有・永続化済 (CanvasStage が1個所有) → 設定はレイアウト非依存
//     に効く。`useRoomChrome` が mount 時にストア値を `engine.setStabilizer` で反映する。
//
// 設計 (`uiScalePreference.ts` / `chatFontScalePreference.ts` / `layoutPreference.ts`
//  と同流儀):
//   - 文字列保存 (`'off' | 'weak' | 'medium' | 'strong'`)。parse 失敗 / 不在 /
//     storage 不可は default ('off') 扱い、例外を throw しない。
//   - StorageLike 抽象 + sentinel probe で Safari private browsing / Quota を弾く。
//   - store factory は test で storage mock を注入可能。
//
// `StabilizerLevel` 型は engine (`infrastructure/drawing/CanvasDrawingEngine`、
// 実体は `PointFilter`) を **single source of truth** として import する (= ストアと
// engine API でレベル enum を二重定義しない、`engine.setStabilizer` にそのまま渡せる)。
//
// Tests: stabilizerPreference.test.ts (storage-injection style)。

import type { StabilizerLevel } from './drawing/CanvasDrawingEngine.js';

export type { StabilizerLevel };

/**
 * デフォルト値 (= off)。有効化しない限り現挙動と同じ (= Classic 含め無退行、§9.8)。
 *
 * 【tri-state 化 (consult 2026-06-13、modern-layout.md §1.1 表現パリティ)】
 * 設定値モデルは AA (`aaPreference.ts`) と同じ **tri-state (未設定 | 明示 level)**:
 *   - 明示設定 (= 保存値あり) はレイアウト間で共有 (ユーザーの選択を layout 跨ぎで尊重)。
 *   - 未設定はレイアウト既定に倒すが、手ぶれ補正は **Classic / Modern とも既定 'off'**
 *     (= Classic は旧 Java 互換の描き味不変、Modern も §9.8 で default off と確定済) の
 *     ため、実効値解決はレイアウト非依存 (`resolveEffectiveStabilizer` 参照)。
 *   - 後方互換: localStorage キーと encoding は不変。「保存値あり = 明示設定」と
 *     再解釈するだけで、既存ユーザーの実効値は変わらない (`get()` ≡
 *     `resolveEffectiveStabilizer(getExplicit())`)。
 */
export const DEFAULT_STABILIZER_LEVEL: StabilizerLevel = 'off';

/**
 * tri-state の実効値解決: 明示設定があればそれ、未設定 (null) なら既定 'off'。
 * AA (`resolveEffectiveAa`) と対称形 (= consult 2026-06-13 続報で AA も共通既定 off に
 * 統一され、どちらもレイアウト引数を取らない)。
 */
export const resolveEffectiveStabilizer = (explicit: StabilizerLevel | null): StabilizerLevel =>
  explicit ?? DEFAULT_STABILIZER_LEVEL;

/** localStorage キー: 手ぶれ補正レベル. */
export const STABILIZER_LEVEL_KEY = 'oekaki:stabilizer-level';

/**
 * 文字列が有効な StabilizerLevel か判定する type guard. unknown / 不正値の defensive
 * narrowing 用.
 */
export const isStabilizerLevel = (v: unknown): v is StabilizerLevel =>
  v === 'off' || v === 'weak' || v === 'medium' || v === 'strong';

/**
 * Minimal Storage shape this module uses (uiScalePreference.ts と同流儀).
 * `globalThis.localStorage` matches; tests pass a mock with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface StabilizerPreferenceStore {
  /**
   * 現在の手ぶれ補正レベル (= **実効値**) を読み出し. 不在 / 不正 / storage 不可 →
   * `DEFAULT_STABILIZER_LEVEL`. tri-state の既定がレイアウト非依存 ('off') なので
   * `resolveEffectiveStabilizer(getExplicit())` と同値.
   */
  get(): StabilizerLevel;
  /** 明示設定を読み出し. 不在 / 不正 / storage 不可 → `null` (= 未設定). */
  getExplicit(): StabilizerLevel | null;
  /** 手ぶれ補正レベルを保存. storage 不可なら no-op (例外を throw しない). */
  set(level: StabilizerLevel): void;
  /** key を削除. テスト / 設定リセット用. storage 不可なら no-op. */
  clear(): void;
}

/**
 * `globalThis.localStorage` をプローブして使用可能なら返す, さもなくば `null`.
 * uiScalePreference.ts の probe を流用 (sentinel write + readback で Safari
 * private browsing / QuotaExceeded を弾く).
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:stabilizer-level__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateStabilizerPreferenceStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= getter always returns DEFAULT_STABILIZER_LEVEL, setter silently drops).
   *
   * テストでは小さな `Map` ベースの fake を渡す.
   */
  storage?: StorageLike | null;
}

const readStabilizerExplicit = (storage: StorageLike): StabilizerLevel | null => {
  try {
    const raw = storage.getItem(STABILIZER_LEVEL_KEY);
    if (raw === null) return null;
    // 不正値は未設定扱い (= 既定に解決される。旧 get() の fallback と実効値一致)。
    return isStabilizerLevel(raw) ? raw : null;
  } catch {
    return null;
  }
};

const writeStabilizerLevel = (storage: StorageLike, level: StabilizerLevel): void => {
  try {
    storage.setItem(STABILIZER_LEVEL_KEY, level);
  } catch {
    // QuotaExceeded / SecurityError 等 — 永続化できないだけで動作は継続させる.
  }
};

/**
 * `StabilizerPreferenceStore` のインスタンスを構築. storage が unavailable な環境
 * (SSR / Safari private browsing 厳格モード) でも getter は default を返すだけで、
 * setter は no-op (= 例外で UI が壊れることはない).
 */
export const createStabilizerPreferenceStore = (
  opts: CreateStabilizerPreferenceStoreOptions = {},
): StabilizerPreferenceStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    get() {
      if (storage === null) return DEFAULT_STABILIZER_LEVEL;
      return resolveEffectiveStabilizer(readStabilizerExplicit(storage));
    },
    getExplicit() {
      if (storage === null) return null;
      return readStabilizerExplicit(storage);
    },
    set(level) {
      if (storage === null) return;
      writeStabilizerLevel(storage, level);
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(STABILIZER_LEVEL_KEY);
      } catch {
        // ignore
      }
    },
  };
};
